<template>
  <section class="hero-section">
    <div class="hero-bg-pattern"></div>
    
    <div class="container hero-content">
      <div class="hero-inner">
        <div class="badge">
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
          </svg>
          GitHub for QA Professionals
        </div>
        
        <h1 class="hero-title">Build Your QA Portfolio.<br>Get Discovered.</h1>
        
        <p class="hero-subtitle">Practice on real scenarios. Earn badges. Build a portfolio that proves your skills—not just claims them.</p>
        
        <!-- Dynamic Pain Point Messages -->
        <div :class="['dynamic-message', { active: selectedExperience === 'beginner' }]">
          <p>🎯 <strong>Break into QA without the catch-22.</strong> Get real experience without needing a job first.</p>
        </div>
        <div :class="['dynamic-message', { active: selectedExperience === 'intermediate' }]">
          <p>🚀 <strong>Level up faster.</strong> Prove your growing expertise with verified project work.</p>
        </div>
        <div :class="['dynamic-message', { active: selectedExperience === 'experienced' }]">
          <p>⭐ <strong>Stand out from 100s of similar resumes.</strong> Showcase depth that certificates can't capture.</p>
        </div>
        <div :class="['dynamic-message', { active: selectedExperience === 'career-switcher' }]">
          <p>🔄 <strong>Prove your skills without starting over.</strong> Your portfolio speaks louder than your past titles.</p>
        </div>
        <div :class="['dynamic-message', { active: selectedExperience === 'student' }]">
          <p>📚 <strong>Graduate with more than just a degree.</strong> Enter the job market with a proven portfolio.</p>
        </div>
        
        <div class="cta-form">
          <div v-if="!formSubmitted">
            <h3 class="form-title">Join the Waitlist</h3>
            <p class="form-subtitle">Be the first to build your verified QA portfolio.</p>
            
            <form @submit.prevent="handleSubmit">
              <div class="form-group">
                <input 
                  v-model="formData.name" 
                  type="text" 
                  placeholder="Your Name" 
                  required 
                  class="form-input"
                >
              </div>
              
              <div class="form-group">
                <input 
                  v-model="formData.email" 
                  type="email" 
                  placeholder="Email Address" 
                  required 
                  class="form-input"
                >
              </div>
              
              <div class="form-group">
                <select 
                  v-model="formData.experience" 
                  required 
                  class="form-select"
                  @change="handleExperienceChange"
                >
                  <option value="" disabled>Your QA Experience</option>
                  <option value="beginner">Beginner (0-1 years)</option>
                  <option value="intermediate">Intermediate (1-3 years)</option>
                  <option value="experienced">Experienced (3+ years)</option>
                  <option value="career-switcher">Career Switcher</option>
                  <option value="student">Student</option>
                </select>
              </div>
              
              <button 
                type="submit" 
                :disabled="isSubmitting" 
                class="submit-btn"
              >
                {{ isSubmitting ? 'Joining...' : 'Join Waitlist' }}
              </button>
              
              <div class="privacy-note">
                <p>Join <strong>500+</strong> QA professionals on the waitlist</p>
                <div class="privacy-badge">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                  No spam, ever
                </div>
              </div>
            </form>
          </div>
          
          <div v-else class="success-message">
            <div class="success-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h3 class="form-title">You're In!</h3>
            <p class="form-subtitle">Check your inbox for a confirmation email. We'll notify you as soon as early access opens.</p>
          </div>
        </div>
        
        <div class="trust-indicators">
          <div class="trust-item">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
            </svg>
            Secure & Private
          </div>
          <div class="trust-item">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Verified Skills
          </div>
          <div class="trust-item">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
            </svg>
            Free to Start
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// API endpoint constant
const API_ENDPOINT = 'https://services.mviaso.com/api/email/waitlist';

export default {
  name: 'HeroSection',
  data() {
    return {
      formData: {
        name: '',
        email: '',
        experience: ''
      },
      selectedExperience: '',
      isSubmitting: false,
      formSubmitted: false
    }
  },
  methods: {
    handleExperienceChange() {
      this.selectedExperience = this.formData.experience;
    },
    async handleSubmit() {
      this.isSubmitting = true;
      
      try {
        const response = await fetch(API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: this.formData.name,
            email: this.formData.email,
            experience: this.formData.experience,
            timestamp: new Date().toISOString()
          })
        });

        if (!response.ok) {
          throw new Error('Failed to submit form');
        }
        
        this.formSubmitted = true;
      } catch (error) {
        console.error('Error submitting form:', error);
        alert('There was an error submitting your form. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    }
  }
}
</script>
